import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { useAuth } from "./contexts/AuthContext";
import { useGoogleAnalytics } from "./hooks/useGoogleAnalytics";
import NotFound from "pages/NotFound";

// Lazy load page components for code splitting
const InteractiveAssessmentPortal = lazy(() => import('./pages/interactive-assessment-portal'));
const CareerMatchingEngine = lazy(() => import('./pages/career-matching-engine'));
const PersonalDashboard = lazy(() => import('./pages/personal-dashboard'));
const ProfessionalServices = lazy(() => import('./pages/professional-services'));
const Homepage = lazy(() => import('./pages/homepage'));
const EntrepreneurialAssessmentHub = lazy(() => import('./pages/entrepreneurial-assessment-hub'));
const BusinessConsultingIntegrationPortal = lazy(() => import('./pages/business-consulting-integration-portal'));
const EntrepreneurialGrowthDashboard = lazy(() => import('./pages/entrepreneurial-growth-dashboard'));
const WorkRelationshipsHub = lazy(() => import('./pages/work-relationships-hub'));
const PaymentBookingDashboard = lazy(() => import('./pages/payment-booking-dashboard'));
const SignIn = lazy(() => import('./pages/auth/SignIn'));
const SignUp = lazy(() => import('./pages/auth/SignUp'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const GDPRCompliance = lazy(() => import('./pages/legal/GDPRCompliance'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FEFEFE]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00004d] mx-auto mb-4"></div>
      <p className="text-gray-600">Se încarcă...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return children;
};

const RoutesContent = () => {
  useGoogleAnalytics();

  return (
    <Suspense fallback={<PageLoader />}>
      <RouterRoutes>
        <Route path="/" element={<Homepage />} />
        <Route path="/homepage" element={<Homepage />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/entrepreneurial-assessment-hub" element={<EntrepreneurialAssessmentHub />} />
        <Route path="/business-consulting-integration-portal" element={<BusinessConsultingIntegrationPortal />} />
        <Route path="/professional-services" element={<ProfessionalServices />} />
        <Route path="/work-relationships-hub" element={<WorkRelationshipsHub />} />
        <Route path="/career-matching-engine" element={<CareerMatchingEngine />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/gdpr-compliance" element={<GDPRCompliance />} />
        <Route 
          path="/interactive-assessment-portal" 
          element={
            <ProtectedRoute>
              <InteractiveAssessmentPortal />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/personal-dashboard" 
          element={
            <ProtectedRoute>
              <PersonalDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/entrepreneurial-growth-dashboard" 
          element={
            <ProtectedRoute>
              <EntrepreneurialGrowthDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment-booking-dashboard" 
          element={
            <ProtectedRoute>
              <PaymentBookingDashboard />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </Suspense>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RoutesContent />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
